import React, { useEffect, useState } from 'react';
import { useScheduleManagement } from '../../Contexts/Context_ScheduleManagement';
import { useAccounts } from '../../Contexts/Context_Accounts';
import { useMapRenderer } from '../../Contexts/Context_MapRenderer';
import UI_Icon from './UI_Icons';

export function UI_LogGroup_Item_Generate({ Logs }) {

}

// --- 1. Move getObjectDiff OUTSIDE the component ---
// This is a pure utility function and doesn't need to be
// re-defined on every render.
const getObjectDiff = (original, updated) => {
    if (typeof original !== 'object' || original === null ||
        typeof updated !== 'object' || updated === null) {
        return [];
    }

    const allKeys = new Set([...Object.keys(original), ...Object.keys(updated)]);
    const changes = [];

    allKeys.forEach(key => {
        const originalValue = original[key];
        const updatedValue = updated[key];

        // --- THIS IS THE FIX ---
        // We can't just use !== for objects.
        let areEqual;
        if (typeof originalValue === 'object' && originalValue !== null && typeof updatedValue === 'object' && updatedValue !== null) {
            // If both are objects, compare their JSON string value
            areEqual = JSON.stringify(originalValue) === JSON.stringify(updatedValue);
        } else {
            // Otherwise, use a strict equality check
            areEqual = originalValue === updatedValue;
        }

        if (!areEqual) { // Only add if they are NOT equal
            changes.push({
                field: key,
                from: originalValue,
                to: updatedValue
            });
        }
        // --- END FIX ---
    });

    return changes;
};

function formatDiffValue(value) {
    if (value === undefined) {
        return "(not set)";
    }
    if (value === null) {
        return "(null)";
    }
    if (typeof value === 'string' && value.trim() === "") {
        return "(empty string)";
    }
    if (Array.isArray(value)) {
        if (value.length === 0) {
            return "(empty list)";
        }
        return `[...${value.length} items]`;
    }
    if (typeof value === 'object') {
        // This prevents the "[object Object]" spam
        return "(details changed)";
    }

    return String(value);
}


export function UI_LogGroup_Item({
    Item,
    Index,
    Render = {
        Pill: "Enabled"
    }
}) {
    // --- 2. Call hooks ONCE at the top level ---
    // Context_Parse will get these functions from its parent scope (closure).
    const { Time_Parse, Time_Range_Parse } = useScheduleManagement();
    const { Account_Fetch_ByID_FromState } = useAccounts();
    const { Room_Get_Address } = useMapRenderer();

    // --- 3. Define Context_Parse as a nested helper ---
    const Context_Parse = (Data) => {
        // The hooks (Time_Parse, etc.) are available from the parent.
        const Action = Data.Action;
        const Context = Data.Context;

        if (!Context) {
            return { type: "none" };
        }

        if (Action === "SM_Reservation_Reschedule") {
            return {
                type: "simple", // Standardized to lowercase 'type'
                // Standardized to lowercase 'original'/'updated'
                original: Time_Range_Parse(Context.Original.Time_Start, Context.Original.Time_End),
                updated: Time_Range_Parse(Context.Updated.Time_Start, Context.Updated.Time_End)
            }
        } else if (Action === "SM_Reservation_Relocate") {
            return {
                type: "simple",
                original: Room_Get_Address(Context.Original.RoomID),
                updated: Room_Get_Address(Context.Updated.RoomID)
            }
        } else {
            return {type: "None"}
        }
        // --- 4. NEW: Add a specific handler for your "edit" action ---
        // (Replace 'SM_Reservation_Edited' with your actual action name)
        if (Action === "SM_Reservation_Edited" && Context.Original.Edit && Context.Updated.Edit) {

            // This is the key: we diff the *nested* Edit objects
            const diffs = getObjectDiff(Context.Original.Edit, Context.Updated.Edit);

            if (diffs.length > 0) {
                return {
                    type: 'diff',
                    changes: diffs.map(change => {
                        // --- 5. Use your existing formatters for known fields ---
                        if (change.field === 'RoomID') {
                            return {
                                field: 'Room',
                                from: Room_Get_Address(change.from),
                                to: Room_Get_Address(change.to)
                            };
                        }
                        // Format any other known fields here...
                        if (change.field === 'DateModified' || change.field === 'DateCreated') {
                            return {
                                field: change.field,
                                from: Time_Parse(change.from),
                                to: Time_Parse(change.to)
                            };
                        }

                        // --- 6. Use the new formatter for all other fields ---
                        return {
                            ...change,
                            from: formatDiffValue(change.from),
                            to: formatDiffValue(change.to)
                        };
                    })
                };
            }
        }

        // --- 7. MODIFIED: Your generic fallback logic ---
        // This will now only run if the action wasn't a known one.
        // It's still useful, but we'll use the new formatter.
        if (Context.Original && Context.Updated) {
            const diffs = getObjectDiff(Context.Original, Context.Updated);

            if (diffs.length > 0) {
                return {
                    type: 'diff',
                    changes: diffs.map(change => {
                        // We use the new formatter here too
                        return {
                            ...change,
                            from: formatDiffValue(change.from),
                            to: formatDiffValue(change.to)
                        };
                    })
                };
            }
        }

        return { type: "none" }; // Fallback
    }


    // --- 5. Define the renderContext helper ---
    const renderContext = () => {
        // --- 6. Pass 'Item' to Context_Parse ---
        const parsedContext = Context_Parse(Item);

        switch (parsedContext.type) {
            case 'simple':
                return (
                    <div className='Log_Item_Context'>
                        <p className='Log_Item_Context_Text'>
                            from {parsedContext.original} {/* Must match 'original' key */}
                        </p>
                        {/* <UI_Icon Name="Arrow_Right" /> */}
                        <p className='Log_Item_Context_Text'>
                            to &nbsp; {parsedContext.updated} {/* Must match 'updated' key */}
                        </p>
                    </div>
                );

            case 'diff':
                return (
                    <div className='Log_Item_Context_Diff'>
                        {parsedContext.changes.map((change, idx) => (
                            <div className='Log_Item_Context' key={idx}>
                                <span className='Log_Item_Context_Text'>{change.field}:</span>
                                <span className='Log_Item_Context_Text'>{change.from}</span>
                                <UI_Icon Name="Arrow_Right" />
                                <span className='Log_Item_Context_Text'>{change.to}</span>
                            </div>
                        ))}
                    </div>
                );

            case 'none':
            default:
                return null;
        }
    };

    // --- Main Component Render ---
    return (
        <div className='Log_Item' key={`Log_${Index}`}>
            <div className='Log_Item_Side'>
                <div className='Log_Item_Side_Pill' State={Render?.Pill}></div>
                <div className='Log_Item_Side_Line'></div>
            </div>
            <div className='Log_Item_Content'>
                <p className='Log_Item_Timestamp_Text'>
                    {Time_Parse(Item.TimeStamp)}
                </p>
                <p className='Log_Item_Actor_Text'>
                    by {Account_Fetch_ByID_FromState(Item.Actor)?.Name}
                </p>
                <p className='Log_Item_Message_Text'>
                    {Item.Message}
                </p>

                {/* --- 7. CRITICAL FIX: Use renderContext() --- */}
                {/* This replaces your old "Item.Context ?" block */}
                {Item.Context && renderContext()}

            </div>
        </div>
    )
}



export default function UI_LogGroup({
    Logs,
    Paginateable
}) {
    const { Time_Parse, Time_Range_Parse } = useScheduleManagement();
    const { Account_Fetch_ByID_FromState } = useAccounts();
    const { Room_Get_Address } = useMapRenderer();


    const Log_Items = Logs.map((Item, Index) => (
        <UI_LogGroup_Item Item={Item} Index={Index} />
    ))
    return (
        <>
            {!Paginateable ?
                <div className='Logs'>
                    {Log_Items}
                </div>
                :
                <>
                    {Log_Items}
                </>

            }

        </>
    )
}